import { useEffect, useRef } from 'react';

import '@harmonyso/conversation-viewer';
import type {
  ConversationEnvelope,
  HarmonyConversationElement,
} from '@harmonyso/conversation-viewer';

/** Module-level so the effect's dep array is stable across renders. */
const NO_TRACKS: readonly string[] = [];

interface ConversationViewProps {
  data: ConversationEnvelope;
  /** Tracks to switch on at mount: tool, think, state, prompt, internal.
   *  Chat is always shown. Viewer changes are remembered per-browser. */
  show?: readonly string[];
  className?: string;
}

/**
 * The shared conversation viewer, from @harmonyso/conversation-viewer.
 *
 * It is a custom element rather than a React component so the same renderer
 * serves this app, `conv` locally, and FastAPI-served report pages. It takes
 * its data through a property (not an attribute), so it has to be assigned
 * imperatively. It reads --background/--foreground/--muted/--border/--primary
 * off the host, so it inherits the shadcn theme with no configuration.
 */
export function ConversationView({ data, show = NO_TRACKS, className }: ConversationViewProps) {
  const ref = useRef<HarmonyConversationElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    for (const g of show) el.setAttribute(`show-${g}`, '');
    el.data = data;
  }, [data, show]);

  return <harmony-conversation ref={ref} className={className} />;
}
